// src/services/clientService.ts
// Client service - Supabase-first implementation

import { supabase } from "@/lib/supabaseClient";
import type { Tables, TablesInsert, TablesUpdate } from "@/lib/supabaseClient";
import type { Client } from "@/types/client";
import { clientNameToSlug } from "@/types/client";
import { isSupabaseNotFound } from "@/utils/apiErrors";

export type { Client } from "@/types/client";

// Database row type from schema
type ClientRow = Tables<"clients">;
type ClientInsert = TablesInsert<"clients">;
type ClientUpdate = TablesUpdate<"clients">;

/**
 * Map database row to Client type used in the app
 * Converts snake_case database fields to camelCase
 */
function mapRowToClient(row: ClientRow): Client {
  return {
    id: row.id,
    name: row.name,
    slug: (row as any).slug ?? undefined,
    shortName: row.short_name ?? undefined,
    primaryContactName: row.primary_contact_name ?? undefined,
    primaryContactEmail: row.primary_contact_email ?? undefined,
    notes: row.notes ?? undefined,
    enabledModules: (row.enabled_modules ?? [
      "core",
    ]) as Client["enabledModules"],
    pricingTier: (row.pricing_tier ?? "standard") as Client["pricingTier"],
    moduleSettings: (row.module_settings as Record<string, unknown>) ?? {},
    isDemo: (row as ClientRow & { is_demo?: boolean }).is_demo ?? false,
    isProtected:
      (row as ClientRow & { is_protected?: boolean }).is_protected ?? false,
    createdAt: new Date(row.created_at).getTime(),
    updatedAt: new Date(row.updated_at).getTime(),
  };
}

/**
 * Map Client type to database insert format
 */
function mapClientToInsert(
  client: Omit<Client, "id" | "createdAt" | "updatedAt">,
): ClientInsert {
  return {
    name: client.name,
    slug: client.slug ?? clientNameToSlug(client.name),
    short_name: client.shortName ?? null,
    primary_contact_name: client.primaryContactName ?? null,
    primary_contact_email: client.primaryContactEmail ?? null,
    notes: client.notes ?? null,
    enabled_modules: client.enabledModules ?? ["core"],
    pricing_tier: client.pricingTier ?? "standard",
    module_settings: (client.moduleSettings ?? {}) as never,
    is_active: true,
    brand: {},
    settings: {},
  };
}

/**
 * Map Client type to database update format
 */
function mapClientToUpdate(
  client: Partial<Omit<Client, "id" | "createdAt" | "updatedAt">>,
): ClientUpdate {
  const update: ClientUpdate = {};

  if (client.name !== undefined) {
    update.name = client.name;
    // Auto-update slug when name changes, unless an explicit slug is provided
    (update as any).slug = client.slug ?? clientNameToSlug(client.name);
  }
  if (client.slug !== undefined) (update as any).slug = client.slug;
  if (client.shortName !== undefined) update.short_name = client.shortName;
  if (client.primaryContactName !== undefined)
    update.primary_contact_name = client.primaryContactName;
  if (client.primaryContactEmail !== undefined)
    update.primary_contact_email = client.primaryContactEmail;
  if (client.notes !== undefined) update.notes = client.notes;
  if (client.enabledModules !== undefined)
    update.enabled_modules = client.enabledModules;
  if (client.pricingTier !== undefined)
    update.pricing_tier = client.pricingTier;
  if (client.moduleSettings !== undefined)
    update.module_settings = client.moduleSettings as never;

  return update;
}

/**
 * Client Service - Supabase-backed implementation
 *
 * All methods include error handling and proper type mapping
 * Uses RLS (Row Level Security) for authorization
 */
export const clientService = {
  /**
   * List all clients accessible to the current user
   * Only returns active clients (not soft-deleted)
   *
   * @throws Error if database query fails
   */
  list: async (): Promise<Client[]> => {
    const { data, error } = await supabase
      .from("clients")
      .select("*")
      .is("deleted_at", null)
      .eq("is_active", true)
      .order("name", { ascending: true });

    if (error) {
      console.error("Error fetching clients:", error);
      throw new Error(`Failed to fetch clients: ${error.message}`);
    }

    return (data || []).map(mapRowToClient);
  },

  /**
   * Get all clients (alias for list)
   * Maintained for backward compatibility
   */
  getAllClients: async (): Promise<Client[]> => {
    return clientService.list();
  },

  /**
   * Get a single client by ID
   *
   * @param id - Client UUID
   * @returns Client or null if not found
   * @throws Error if database query fails
   */
  getById: async (id: string): Promise<Client | null> => {
    const { data, error } = await supabase
      .from("clients")
      .select("*")
      .eq("id", id)
      .is("deleted_at", null)
      .single();

    if (error) {
      if (isSupabaseNotFound(error)) {
        // Not found
        return null;
      }
      console.error("Error fetching client:", error);
      throw new Error(`Failed to fetch client: ${error.message}`);
    }

    return data ? mapRowToClient(data) : null;
  },

  /**
   * Get a single client by slug OR UUID.
   *
   * IMPORTANT: This function accepts BOTH slugs AND UUIDs for backwards compatibility
   * with legacy bookmarks that contain UUIDs in the URL. The ClientSync component
   * relies on this behavior to canonicalize URLs.
   *
   * DO NOT remove UUID detection without updating ClientSync and ROUTING.md.
   *
   * Accepts either:
   * - A slug (lowercase, hyphenated version of client name, e.g., "regional-food-bank")
   * - A UUID (e.g., "00000000-0000-0000-0000-000000000003")
   *
   * @param identifier - Client slug or UUID
   * @returns Client or null if not found
   * @throws Error if database query fails
   *
   * @see ClientSync - Uses this for URL canonicalization
   * @see ROUTING.md - Documents the UUID handling behavior
   */
  getBySlugOrId: async (identifier: string): Promise<Client | null> => {
    if (!identifier) {
      console.error("Client not found for identifier: undefined");
      return null;
    }

    // Skip reserved paths that are never valid client slugs
    // These can appear during dev server requests or misconfigured routes
    const RESERVED_SLUGS = new Set([
      "build",
      "assets",
      "static",
      "public",
      "api",
      "node_modules",
      "src",
      "dist",
      ".well-known",
    ]);

    if (RESERVED_SLUGS.has(identifier.toLowerCase())) {
      // Silent return - these are expected in dev environments
      return null;
    }

    // Check if identifier is a UUID (basic pattern check)
    const isUUID =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        identifier,
      );

    // If it's a UUID, use getById for direct lookup
    if (isUUID) {
      return clientService.getById(identifier);
    }

    // Otherwise, treat it as a slug and do a direct slug column lookup
    const { data, error } = await supabase
      .from("clients")
      .select("*")
      .eq("slug", identifier)
      .is("deleted_at", null)
      .maybeSingle();

    if (error) {
      console.error("Error fetching client by slug:", error);
      return null;
    }

    if (!data) {
      console.error(`Client not found for identifier: ${identifier}`);
      return null;
    }

    return mapRowToClient(data);
  },

  /**
   * @deprecated Use getBySlugOrId instead. This alias exists for backward compatibility.
   */
  getBySlug: async (identifier: string): Promise<Client | null> => {
    return clientService.getBySlugOrId(identifier);
  },

  /**
   * Create a new client
   *
   * @param clientData - Client data (without id, createdAt, updatedAt)
   * @returns Newly created client
   * @throws Error if database insert fails
   */
  createClient: async (
    clientData: Omit<Client, "id" | "createdAt" | "updatedAt">,
  ): Promise<Client> => {
    const insert = mapClientToInsert(clientData);

    const { data, error } = await supabase
      .from("clients")
      .insert(insert as never)
      .select()
      .single();

    if (error) {
      console.error("Error creating client:", error);
      throw new Error(`Failed to create client: ${error.message}`);
    }

    if (!data) {
      throw new Error("Client created but no data returned");
    }

    return mapRowToClient(data as ClientRow);
  },

  /**
   * Update an existing client
   * Protected clients cannot have their name changed
   *
   * @param id - Client UUID
   * @param updates - Partial client data to update
   * @returns Updated client
   * @throws Error if database update fails or client is protected
   */
  updateClient: async (
    id: string,
    updates: Partial<Omit<Client, "id" | "createdAt" | "updatedAt">>,
  ): Promise<Client> => {
    // Check if client is protected before updating
    const existing = await clientService.getById(id);
    if (
      existing?.isProtected &&
      updates.name &&
      updates.name !== existing.name
    ) {
      throw new Error("Cannot rename protected demo organization");
    }

    const update = mapClientToUpdate(updates);

    const { data, error } = await supabase
      .from("clients")
      .update(update as never)
      .eq("id", id)
      .is("deleted_at", null)
      .select()
      .single();

    if (error) {
      console.error("Error updating client:", error);
      throw new Error(`Failed to update client: ${error.message}`);
    }

    if (!data) {
      throw new Error("Client not found or update failed");
    }

    return mapRowToClient(data as ClientRow);
  },

  /**
   * Soft delete a client
   * Sets deleted_at timestamp instead of actually deleting the row
   * Protected clients cannot be deleted
   *
   * @param id - Client UUID
   * @throws Error if database update fails or client is protected
   */
  delete: async (id: string): Promise<void> => {
    // Check if client is protected before deleting
    const existing = await clientService.getById(id);
    if (existing?.isProtected) {
      throw new Error("Cannot delete protected demo organization");
    }

    const { error } = await supabase
      .from("clients")
      .update({ deleted_at: new Date().toISOString() } as never)
      .eq("id", id);

    if (error) {
      console.error("Error deleting client:", error);
      throw new Error(`Failed to delete client: ${error.message}`);
    }
  },

  /**
   * Permanently delete a client (hard delete)
   * Use with caution - this cannot be undone
   * Protected clients cannot be deleted
   *
   * @param id - Client UUID
   * @throws Error if database delete fails or client is protected
   */
  hardDelete: async (id: string): Promise<void> => {
    // Check if client is protected before deleting
    const existing = await clientService.getById(id);
    if (existing?.isProtected) {
      throw new Error("Cannot permanently delete protected demo organization");
    }

    const { error } = await supabase.from("clients").delete().eq("id", id);

    if (error) {
      console.error("Error permanently deleting client:", error);
      throw new Error(`Failed to permanently delete client: ${error.message}`);
    }
  },

  /**
   * Restore a soft-deleted client
   *
   * @param id - Client UUID
   * @returns Restored client
   * @throws Error if database update fails
   */
  restore: async (id: string): Promise<Client> => {
    const { data, error } = await supabase
      .from("clients")
      .update({ deleted_at: null } as never)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("Error restoring client:", error);
      throw new Error(`Failed to restore client: ${error.message}`);
    }

    if (!data) {
      throw new Error("Client not found");
    }

    return mapRowToClient(data as ClientRow);
  },
};

// Backward compatibility exports
export const listClients = () => clientService.getAllClients();
export const deleteClient = (id: string) => clientService.delete(id);
export const createClient = (
  data: Omit<Client, "id" | "createdAt" | "updatedAt">,
) => clientService.createClient(data);
export const updateClient = (
  id: string,
  data: Partial<Omit<Client, "id" | "createdAt" | "updatedAt">>,
) => clientService.updateClient(id, data);
